import { ModalController } from "@theme/modal-controller";

const DISMISSED_STORAGE_KEY = "localization_selector_dismissed";
const PROMPTED_STORAGE_KEY = "localization_selector_prompted";
const VISITED_STORAGE_KEY = "golfposer_site_visited";
let locationDetectionInitiated = false;

function hasVisitedBefore() {
  try {
    return (
      localStorage.getItem(VISITED_STORAGE_KEY) === "true" ||
      localStorage.getItem(PROMPTED_STORAGE_KEY) === "true" ||
      localStorage.getItem(DISMISSED_STORAGE_KEY) === "true" ||
      sessionStorage.getItem(DISMISSED_STORAGE_KEY) === "true" ||
      sessionStorage.getItem("localization_form_submitted") === "true"
    );
  } catch {
    return false;
  }
}

function isDismissed() {
  return hasVisitedBefore();
}

function markVisited() {
  try {
    localStorage.setItem(VISITED_STORAGE_KEY, "true");
  } catch {
    // Ignore storage availability errors
  }
}

function setDismissed() {
  try {
    localStorage.setItem(DISMISSED_STORAGE_KEY, "true");
    localStorage.setItem(PROMPTED_STORAGE_KEY, "true");
    localStorage.setItem(VISITED_STORAGE_KEY, "true");
    sessionStorage.setItem(DISMISSED_STORAGE_KEY, "true");
    sessionStorage.setItem("localization_form_submitted", "true");
    sessionStorage.removeItem("qf_submitted_modal");
  } catch {
    // Ignore storage availability errors
  }
}

// Listen for modal close events, clicks on close triggers, and Escape key
if (typeof window !== "undefined") {
  window.addEventListener("location-selector-id-modal-closed", () => {
    setDismissed();
    const modal = document.querySelector('[data-modal="location-selector-id"]');
    const locForm = modal?.querySelector("localization-form");
    if (locForm && typeof locForm.resetToCurrentMarket === "function") {
      locForm.resetToCurrentMarket();
    }
  });

  document.addEventListener("click", (event) => {
    const confirmBtn = event.target.closest("[data-location-confirm]");
    if (confirmBtn) {
      event.preventDefault();
      const modal = document.querySelector('[data-modal="location-selector-id"]');
      if (!modal) return;

      setDismissed();
      try {
        sessionStorage.removeItem("qf_submitted_modal");
      } catch {}

      const form = modal.querySelector("#country-localization-form") || modal.querySelector("form");
      const countryInput = form?.querySelector('input[name="country_code"]');
      const initialCountry = (
        countryInput?.dataset.initialValue ||
        form?.dataset.initialCountry ||
        window.Theme?.localization?.country?.iso_code ||
        window.Shopify?.country ||
        ""
      ).toUpperCase();
      const selectedCountry = (countryInput?.value || "").toUpperCase();

      const countryChanged =
        (selectedCountry && initialCountry && selectedCountry !== initialCountry) ||
        countryInput?.dataset.userSelected === "true";

      const langForm = modal.querySelector('form input[name="language_code"]')?.closest("form");
      const langInput = modal.querySelector('input[name="language_code"]');
      const initialLang = (
        langInput?.dataset.initialValue ||
        langForm?.dataset.initialLanguage ||
        ""
      ).toLowerCase();
      const selectedLang = (langInput?.value || "").toLowerCase();
      const langChanged =
        (selectedLang && initialLang && selectedLang !== initialLang) ||
        langInput?.dataset.userSelected === "true";

      if (countryChanged || langChanged) {
        if (form) {
          if (selectedLang && !form.querySelector('input[name="language_code"]')) {
            const hiddenLang = document.createElement("input");
            hiddenLang.type = "hidden";
            hiddenLang.name = "language_code";
            hiddenLang.value = selectedLang;
            form.appendChild(hiddenLang);
          }
          if (typeof form.requestSubmit === "function") {
            form.requestSubmit();
          } else {
            form.submit();
          }
        } else if (langForm) {
          if (typeof langForm.requestSubmit === "function") {
            langForm.requestSubmit();
          } else {
            langForm.submit();
          }
        }
      } else {
        const modalController = new ModalController();
        modalController.hideModal("location-selector-id");
      }
      return;
    }

    if (
      event.target.closest('[data-modal-close="location-selector-id"]') ||
      (event.target.closest('.modal-overlay[data-modal="location-selector-id"]') &&
        !event.target.closest(".modal-panel"))
    ) {
      setDismissed();
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      const modal = document.querySelector('[data-modal="location-selector-id"]');
      if (modal && !modal.hasAttribute("hidden") && modal.getAttribute("aria-hidden") !== "true") {
        setDismissed();
        const modalController = new ModalController();
        modalController.hideModal("location-selector-id");
      }
    }
  });
}

const COOKIE_BANNER_SELECTORS = [
  ".shopify-pc__banner__dialog",
  ".shopify-pc__banner",
  "#shopify-pc__banner",
  "[class*='shopify-pc__banner']",
  "#tracking-consent-banner",
  "#shopify-consent-banner",
].join(", ");

const POST_COOKIE_DELAY_MS = 1000;

function isCookieBannerActive() {
  const banner = document.querySelector(COOKIE_BANNER_SELECTORS);
  if (!banner) return false;

  try {
    const style = window.getComputedStyle(banner);
    if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") {
      return false;
    }
    const rect = banner.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  } catch {
    return true;
  }
}

async function isCookieBannerPresentOrPending() {
  if (isCookieBannerActive()) return true;

  try {
    if (window.Shopify?.customerPrivacy?.shouldShowBanner?.() === true) {
      return true;
    }
  } catch {
    // Ignore API errors
  }

  // If customerPrivacy API is still loading and document is not yet complete, wait briefly for banner injection
  if (!window.Shopify?.customerPrivacy && document.readyState !== "complete") {
    await new Promise((resolve) => setTimeout(resolve, 400));
    if (isCookieBannerActive()) return true;
    try {
      if (window.Shopify?.customerPrivacy?.shouldShowBanner?.() === true) {
        return true;
      }
    } catch {
      // Ignore API errors
    }
  }

  return false;
}

function presentLocationModal() {
  if (isDismissed()) return;

  const modal = document.querySelector('[data-modal="location-selector-id"]');
  if (!modal) {
    console.warn("Location selector modal not found in DOM");
    return;
  }

  // Avoid presenting if already open
  if (!modal.hasAttribute("hidden") && modal.getAttribute("aria-hidden") !== "true") {
    return;
  }

  // Ensure current market is selected by default in any localization-form inside modal
  const locForm = modal.querySelector("localization-form");
  if (locForm && typeof locForm.resetToCurrentMarket === "function") {
    locForm.resetToCurrentMarket();
  }

  // Mark as prompted and visited so returning visits do not trigger automatic popups
  setDismissed();

  const modalController = new ModalController();
  modalController.presentModal("location-selector-id");
}

function queueLocationModalAfterCookieConsent() {
  let handled = false;

  const triggerModalWithDelay = () => {
    if (handled) return;
    handled = true;

    cleanup();

    setTimeout(() => {
      if (isDismissed()) return;
      if (!isCookieBannerActive()) {
        presentLocationModal();
      } else {
        // If banner is still animating out, check periodically up to 5 seconds
        const interval = setInterval(() => {
          if (!isCookieBannerActive()) {
            clearInterval(interval);
            if (!isDismissed()) {
              presentLocationModal();
            }
          }
        }, 300);
        setTimeout(() => clearInterval(interval), 5000);
      }
    }, POST_COOKIE_DELAY_MS);
  };

  const onConsentEvent = () => {
    triggerModalWithDelay();
  };

  const onBannerClick = (event) => {
    if (
      event.target.closest(
        "button#shopify-pc__banner__btn-accept, .shopify-pc__banner__btn-accept, " +
        "button#shopify-pc__banner__btn-decline, .shopify-pc__banner__btn-decline, " +
        "[id*='shopify-pc__banner__btn']"
      )
    ) {
      triggerModalWithDelay();
    }
  };

  // Observe DOM for banner removal or visibility toggling
  const observer = new MutationObserver(() => {
    if (!isCookieBannerActive()) {
      triggerModalWithDelay();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["style", "class", "hidden", "aria-hidden"],
  });

  document.addEventListener("visitorConsentCollected", onConsentEvent, { once: true });
  document.addEventListener("click", onBannerClick, true);

  const cleanup = () => {
    observer.disconnect();
    document.removeEventListener("visitorConsentCollected", onConsentEvent);
    document.removeEventListener("click", onBannerClick, true);
  };
}

class LocalizationForm extends HTMLElement {
  constructor() {
    super();
    this.elements = {
      input: this.querySelector('input[name="language_code"], input[name="country_code"]'),
      button: this.querySelector("button"),
      panel: this.querySelector(".country-dropdown-panel") || this.querySelector("ul"),
    };
    this.elements.button?.addEventListener("click", this.openSelector.bind(this));
    this.addEventListener("keyup", this.onContainerKeyUp.bind(this));
    this.querySelectorAll("a").forEach((item) =>
      item.addEventListener("click", this.onItemClick.bind(this)),
    );

    // Close dropdown when clicking outside
    this._handleOutsideClick = (event) => {
      if (!this.contains(event.target)) {
        this.hidePanel();
      }
    };
    document.addEventListener("click", this._handleOutsideClick);

    this.setupSearch();

    if (!isDismissed() && !locationDetectionInitiated) {
      locationDetectionInitiated = true;
      this.detectLocation();
    }
  }

  disconnectedCallback() {
    if (this._handleOutsideClick) {
      document.removeEventListener("click", this._handleOutsideClick);
    }
  }

  setupSearch() {
    const searchInput = this.querySelector(".country-search-input");
    const clearBtn = this.querySelector(".country-search-clear");
    const emptyState = this.querySelector(".country-search-empty");
    const listItems = this.querySelectorAll(".country-list-item");

    if (!searchInput) return;

    searchInput.addEventListener("input", (e) => {
      const query = e.target.value.trim().toLowerCase();
      let matchCount = 0;

      if (clearBtn) {
        clearBtn.hidden = !query;
      }

      listItems.forEach((li) => {
        const term = li.getAttribute("data-search-term") || "";
        const isMatch = !query || term.includes(query);
        li.style.display = isMatch ? "" : "none";
        if (isMatch) matchCount++;
      });

      if (emptyState) {
        emptyState.hidden = matchCount > 0;
      }
    });

    if (clearBtn) {
      clearBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        searchInput.value = "";
        searchInput.dispatchEvent(new Event("input"));
        searchInput.focus();
      });
    }

    searchInput.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.hidePanel();
        this.elements.button?.focus();
      } else if (e.key === "Enter") {
        e.preventDefault();
        const firstVisibleLink = this.querySelector(
          ".country-list-item:not([style*='display: none']) a"
        );
        if (firstVisibleLink) {
          firstVisibleLink.click();
        }
      }
    });
  }

  hidePanel() {
    this.elements.button?.setAttribute("aria-expanded", "false");
    this.elements.panel?.setAttribute("hidden", "true");
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;
    this.hidePanel();
    this.elements.button?.focus();
  }

  onItemClick(event) {
    event.preventDefault();
    const link = event.currentTarget;
    const value = link.dataset.value;
    const name = link.dataset.name;
    const currencySymbol = link.dataset.currencySymbol;
    const flagUrl = link.dataset.flagUrl;

    if (this.elements.input) {
      this.elements.input.value = value;
      this.elements.input.dataset.userSelected = "true";
    }

    // Update country trigger display if present
    const countryLabel = this.querySelector(".country-button-label");
    if (countryLabel && name) {
      countryLabel.textContent = `${name} (${currencySymbol || ""})`;
    }
    const flagImg = this.querySelector(".country-button-flag img");
    if (flagImg && flagUrl) {
      flagImg.src = flagUrl;
      flagImg.alt = name;
    }

    // Update language trigger display if present
    const langLabel = this.querySelector(".language-button-label");
    if (langLabel && name) {
      langLabel.textContent = name;
    }

    // Update active class in list
    this.querySelectorAll("a").forEach((a) => {
      a.classList.remove("active");
      a.removeAttribute("aria-current");
    });
    link.classList.add("active");
    link.setAttribute("aria-current", "true");

    this.hidePanel();

    // Reset search query
    const searchInput = this.querySelector(".country-search-input");
    if (searchInput && searchInput.value) {
      searchInput.value = "";
      searchInput.dispatchEvent(new Event("input"));
    }

    // Auto-submit only if NOT a modal selector
    if (!this.hasAttribute("data-modal-selector")) {
      const form = this.querySelector("form");
      if (form) {
        setDismissed();
        form.submit();
      }
    }
  }

  openSelector(event) {
    if (!this.elements.button || !this.elements.panel) return;
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const isExpanded = this.elements.button.getAttribute("aria-expanded") === "true";
    if (isExpanded) {
      this.hidePanel();
      return;
    }

    this.elements.panel.removeAttribute("hidden");
    this.elements.button.setAttribute("aria-expanded", "true");

    const searchInput = this.querySelector(".country-search-input");
    if (searchInput) {
      setTimeout(() => {
        searchInput.focus();
      }, 60);
    }

    // Scroll the currently active market item into view in the dropdown list
    const activeItem = this.querySelector(".country-list-item a.active")?.closest(
      ".country-list-item"
    );
    const listContainer = this.querySelector("#CountryList");
    if (activeItem && listContainer) {
      setTimeout(() => {
        listContainer.scrollTop =
          activeItem.offsetTop - listContainer.offsetTop - 20;
      }, 60);
    }
  }

  closeSelector(event) {
    if (!this.elements.button || !this.elements.panel) return;
    const shouldClose = event.relatedTarget && event.relatedTarget.nodeName === "BUTTON";
    if (event.relatedTarget === null || shouldClose) {
      this.hidePanel();
    }
  }

  /**
   * Resets the dropdown selection, trigger button label, and flag to the current market.
   */
  resetToCurrentMarket() {
    const currentCode = (
      this.elements.input?.dataset.initialValue ||
      window.Theme?.localization?.country?.iso_code ||
      window.Shopify?.country ||
      "GB"
    ).toUpperCase();

    const matchingLink = this.querySelector(
      `.country-list-item a[data-value="${currentCode}"]`
    );
    if (!matchingLink) return;

    if (this.elements.input) {
      this.elements.input.value = currentCode;
      this.elements.input.dataset.userSelected = "false";
    }

    const countryLabel = this.querySelector(".country-button-label");
    if (countryLabel && matchingLink.dataset.name) {
      countryLabel.textContent = `${matchingLink.dataset.name} (${matchingLink.dataset.currencySymbol || ""})`;
    }

    const flagImg = this.querySelector(".country-button-flag img");
    if (flagImg && matchingLink.dataset.flagUrl) {
      flagImg.src = matchingLink.dataset.flagUrl;
      flagImg.alt = matchingLink.dataset.name || "";
    }

    this.querySelectorAll(".country-list-item a").forEach((a) => {
      a.classList.remove("active");
      a.removeAttribute("aria-current");
    });
    matchingLink.classList.add("active");
    matchingLink.setAttribute("aria-current", "true");
  }

  async detectLocation() {
    if (isDismissed()) return;

    try {
      const rootUrl =
        window.Shopify?.routes?.root || window.Theme?.routes?.root_url || "/";
      const currentCountry = (
        window.Theme?.localization?.country?.iso_code ||
        window.Shopify?.country ||
        "GB"
      ).toUpperCase();

      const endpointUrl = `${rootUrl}browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true`;

      const response = await fetch(endpointUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Resolve detected visitor country from Shopify geo-location
      const detectedCountry = (
        data.detected_values?.country?.handle ||
        ""
      ).toUpperCase();

      // Show on the first visit from anyone outside the UK, or anyone jumped into a different market
      const isOutsideUK = Boolean(detectedCountry && detectedCountry !== "GB");
      const isMismatchedMarket = Boolean(
        detectedCountry && currentCountry && detectedCountry !== currentCountry
      );

      if (isOutsideUK || isMismatchedMarket) {
        if (isDismissed()) return;

        // Ensure the dropdown is set to the current market by default
        this.resetToCurrentMarket();

        // Present location selector modal with cookie banner coordination
        if (await isCookieBannerPresentOrPending()) {
          queueLocationModalAfterCookieConsent();
        } else {
          presentLocationModal();
        }
      } else {
        // Visitor is inside the UK and viewing the UK store
        markVisited();
      }
    } catch (error) {
      console.error("Error fetching browsing context suggestions:", error);
    }
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", markVisited);
}

customElements.define("localization-form", LocalizationForm);


